import { useEffect, useRef, useState } from 'react';
import { Popover } from 'radix-ui';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import type { WorkspaceState } from '../context';
import { useWorkspace } from '../context';
import { useIntlLocale } from '../intl-locale';
import { WorkspaceIcon } from '../icons';
import { projectsApi } from './api';
import { refreshProjectIfShown } from './use-projects';
import { isNewProjectOpen, openNewProject } from './new-project-request';
import { WHERE_ICON, whereLabel } from './labels';
import { ProjectMark } from './mark';
import type { ProjectPlace, ProjectSummary } from './types';

// ── Places the client has seen, so a chat started from a project can name it
//    before the server has a thread to describe. Display only: the server
//    decides where a chat lives.
const places = new Map<string, ProjectPlace>();
// Where a chat was just placed or moved from here, ahead of the server's thread meta
// (null: moved out). Display only: the server decides where a chat lives.
const threadPlaces = new Map<string, ProjectPlace | null>();

export function rememberProjectPlace(place: ProjectPlace) {
  places.set(place.id, place);
}
export function projectPlace(id: string | undefined | null) {
  return id ? (places.get(id) ?? null) : null;
}
export function rememberThreadPlace(threadId: string, place: ProjectPlace | null) {
  threadPlaces.set(threadId, place);
}
/** What one account saw is never shown to the next: names go with the account or company. */
export function forgetProjectPlaces() {
  places.clear();
  threadPlaces.clear();
}

/** A chat sent from a project's home that the server did not place there (the project went
 * away or stopped being shared meanwhile) is a plain chat: once its turn ends, it stops
 * naming the project, and the thread says where it lives. */
export function letUnplacedProjectGo(
  ws:
    | Pick<
        WorkspaceState,
        'threadId' | 'threadSaved' | 'turnContext' | 'setTurnContext' | 'refreshThreadMeta'
      >
    | null
    | undefined,
  threadId: string,
) {
  if (!ws || ws.threadId !== threadId || !ws.threadSaved || !ws.turnContext.projectId) return;
  const { projectId: _unplaced, ...rest } = ws.turnContext;
  ws.setTurnContext(rest);
  ws.refreshThreadMeta();
}

/** Where the open chat lives: its project when the reader can read it. */
export function chatProject(ws: Pick<WorkspaceState, 'threadMeta' | 'turnContext' | 'threadId'>) {
  // A new chat started in a project, until its first answer confirms the placement.
  const pending = projectPlace(ws.turnContext.projectId);
  if (pending) return pending;
  if (threadPlaces.has(ws.threadId)) return threadPlaces.get(ws.threadId) ?? null;
  return ws.threadMeta?.project ?? null;
}

function PlaceLabel({ place, companyName }: { place: ProjectPlace | null; companyName: string }) {
  return place ? (
    <>
      <ProjectMark name={place.name} tint={place.tint} size="xs" />
      <span>{place.name}</span>
    </>
  ) : (
    <span>{companyName}</span>
  );
}

/**
 * The chat header's first crumb says where the chat lives. For the chat's owner it is
 * also the menu that moves it (rule 4: one project at a time; moving changes no access).
 */
export function ChatPlaceCrumb() {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const place = chatProject(ws);
  const meta = ws.threadMeta;
  const saved = Boolean(meta) || ws.threads.some((thread) => thread.id === ws.threadId);
  const owner = (meta?.access ?? 'owner') === 'owner';
  const runChat = Boolean(meta?.agentRunId);
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  if (!saved || !owner || runChat)
    return place ? (
      <button type="button" className="ws-project-place" onClick={() => ws.openProject(place.id)}>
        <PlaceLabel place={place} companyName={ws.companyName} />
      </button>
    ) : (
      <span>{ws.companyName}</span>
    );
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        ref={trigger}
        className={`ws-project-place ${place ? 'in-project' : 'no-project'}`}
        aria-label={
          place ? formatCopy(copy.inProjectMove, { name: place.name }) : copy.notInProjectMove
        }
        data-testid="chat-place"
      >
        <PlaceLabel place={place} companyName={ws.companyName} />
        <WorkspaceIcon name="down" size={12} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="ws-project-place-panel pc-portal"
          align="start"
          sideOffset={8}
          collisionPadding={16}
          aria-label={copy.whereChat}
          onCloseAutoFocus={(event) => {
            if (isNewProjectOpen()) event.preventDefault();
          }}
        >
          {open && (
            <PlaceMenu current={place} close={() => setOpen(false)} returnFocus={trigger.current} />
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function PlaceMenu({
  current,
  close,
  returnFocus,
}: {
  current: ProjectPlace | null;
  close: () => void;
  returnFocus: HTMLElement | null;
}) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const locale = useIntlLocale();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let live = true;
    void projectsApi
      .list()
      .then(
        (list) =>
          live &&
          setProjects(list.filter((project) => !project.finished || project.id === current?.id)),
      )
      .catch(() => live && setProjects([]));
    input.current?.focus();
    return () => {
      live = false;
    };
  }, [current?.id]);
  const needle = query.trim().toLocaleLowerCase();
  const shown = (projects ?? []).filter(
    (project) => !needle || project.name.toLocaleLowerCase().includes(needle),
  );
  // A search no project is named exactly becomes the new project's name.
  const creating =
    projects !== null &&
    Boolean(needle) &&
    !shown.some((project) => project.name.toLocaleLowerCase() === needle);
  const move = async (target: ProjectSummary | null) => {
    // Out always goes to the server: the owner may no longer be able to read the project.
    if (target && target.id === current?.id) return close();
    setBusy(true);
    try {
      const moved = await projectsApi.moveChat(ws.threadId, target?.id ?? null);
      if (moved.project) rememberProjectPlace(moved.project);
      rememberThreadPlace(ws.threadId, moved.project);
      refreshProjectIfShown(current?.id);
      refreshProjectIfShown(moved.project?.id);
      if (ws.turnContext.projectId) {
        const { projectId: _placed, ...rest } = ws.turnContext;
        ws.setTurnContext(rest);
      }
      ws.refreshThreadMeta();
      ws.notify(
        target
          ? `${copy.movedTo} ${target.name}. ${copy.accessUnchanged}`
          : `${copy.movedOut} ${current?.name ?? copy.theProject}.`,
        null,
        'success',
      );
      close();
    } catch {
      ws.notify(copy.couldntMoveChat, null, 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <header>
        <strong>{copy.whereChat}</strong>
        <small>{copy.whereChatHelp}</small>
      </header>
      <label className="ws-field-search sm">
        <WorkspaceIcon name="search" size={14} />
        <input
          ref={input}
          type="search"
          aria-label={copy.searchCreateProject}
          placeholder={`${copy.searchCreateProject}…`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      <div className="ws-project-place-list" role="listbox" aria-label={copy.projects}>
        {!needle && (
          <button
            type="button"
            role="option"
            aria-selected={!current}
            className="ws-project-place-row"
            disabled={busy}
            onClick={() => void move(null)}
          >
            <span className="ws-project-mark sm" data-tint="plain" aria-hidden="true">
              <WorkspaceIcon name="chat" size={12} />
            </span>
            <span>
              <b>{copy.noProject}</b>
              <small>{formatCopy(copy.inCompanyChats, { company: ws.companyName })}</small>
            </span>
            {!current && <WorkspaceIcon name="check" size={15} />}
          </button>
        )}
        {projects === null && (
          <p className="ws-project-empty" role="status">
            {copy.loadingProjects}
          </p>
        )}
        {shown.map((project) => (
          <button
            key={project.id}
            type="button"
            role="option"
            aria-selected={project.id === current?.id}
            className="ws-project-place-row"
            disabled={busy}
            onClick={() => void move(project)}
          >
            <ProjectMark name={project.name} tint={project.tint} size="sm" />
            <span>
              <b>{project.name}</b>
              <small>
                <WorkspaceIcon name={WHERE_ICON[project.visibility]} size={11} />
                {whereLabel(project.visibility, ws.companyName, locale)}
              </small>
            </span>
            {project.id === current?.id && <WorkspaceIcon name="check" size={15} />}
          </button>
        ))}
      </div>
      <footer>
        <button
          type="button"
          className="ws-project-place-new"
          disabled={busy || projects === null}
          onClick={() => {
            close();
            openNewProject({
              returnFocus,
              chatId: ws.threadId,
              chatTitle:
                ws.threads.find((thread) => thread.id === ws.threadId)?.title ??
                ws.threadMeta?.title,
              ...(creating ? { name: query.trim() } : {}),
            });
          }}
        >
          <span className="ws-project-place-plus" aria-hidden="true">
            <WorkspaceIcon name="plus" size={13} />
          </span>
          {creating
            ? formatCopy(copy.newProjectNamedWithChat, { name: query.trim() })
            : copy.newProjectWithChat}
        </button>
      </footer>
    </>
  );
}

/** Whether the composer has a project chip to show. A project's home names the project above
 * the composer instead; its chat shows the chip once it exists. */
export function hasProjectChips(
  ws: Pick<WorkspaceState, 'threadMeta' | 'turnContext' | 'threadId' | 'threadSaved'>,
) {
  return Boolean(ws.turnContext.newProject || (ws.threadSaved && chatProject(ws)));
}

/** The composer's project chips: the project a chat lives in, and "Making a new project". */
export function ProjectContextChips({ ws, disabled }: { ws: WorkspaceState; disabled: boolean }) {
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const context = ws.turnContext;
  const lived = ws.threadSaved ? chatProject(ws) : null;
  return (
    <>
      {lived && (
        <span
          className="ws-context-chip ws-project-chip"
          title={formatCopy(copy.projectChipLives, { name: lived.name })}
        >
          <WorkspaceIcon name="project" size={12} />
          {lived.name}
        </span>
      )}
      {context.newProject && (
        <button
          type="button"
          className="ws-context-chip"
          disabled={disabled}
          title={copy.stopMakingProject}
          onClick={() => {
            const { newProject: _newProject, ...rest } = context;
            ws.setTurnContext(rest);
          }}
        >
          <WorkspaceIcon name="project" size={13} />
          {copy.makingProject}
          <WorkspaceIcon name="close" size={12} />
        </button>
      )}
    </>
  );
}
